import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { 
  Home, 
  Calendar, 
  DollarSign, 
  FileText, 
  User, 
  Phone, 
  Mail,
  Clock,
  CheckCircle,
  AlertCircle,
  Shield,
  Building
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "$0.00";
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const getStatusBadge = (status) => {
  const styles = {
    paid: "bg-green-500/20 text-green-400 border-green-500/30",
    partial: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    pending: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
    late: "bg-red-500/20 text-red-400 border-red-500/30"
  };
  return styles[status] || styles.pending;
};

const getStatusIcon = (status) => {
  switch (status) {
    case "paid": return <CheckCircle className="w-4 h-4" />;
    case "partial": return <Clock className="w-4 h-4" />;
    case "late": return <AlertCircle className="w-4 h-4" />;
    default: return <Clock className="w-4 h-4" />;
  }
};

export default function TenantPortal() {
  const { accessToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchPortalData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/api/tenant-portal/${accessToken}`);
        setData(response.data);
      } catch (err) {
        const msg = err.response?.data?.detail || "Failed to load portal";
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchPortalData();
    }
  }, [accessToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading your tenant portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-black flex items-center justify-center p-4">
        <Card className="bg-[#0A0A0A] border-red-500/30 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Access Denied</h1>
            <p className="text-zinc-400">{error}</p>
            <p className="text-zinc-500 text-sm mt-4">
              Please contact your property manager for a new access link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { tenant_name, property, unit, lease, rent_ledger } = data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-black">
      {/* Header */}
      <div className="bg-[#0A0A0A] border-b border-white/10 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-xl font-bold text-white">Tenant Portal</h1>
              <p className="text-zinc-500 text-sm">{property.name}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-white font-medium">{tenant_name}</p>
            <p className="text-zinc-500 text-sm">Unit {unit.unit_number}</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Welcome Card */}
        <Card className="bg-gradient-to-r from-primary/20 to-teal-500/20 border-primary/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Welcome, {tenant_name}!</h2>
                <p className="text-zinc-300">
                  {property.address && `${property.address} • `}Unit {unit.unit_number}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Rent Summary Card */}
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <DollarSign className="w-5 h-5 text-primary" /> Rent Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-zinc-500">Monthly Rent</span>
                <span className="text-white font-mono font-bold">{formatCurrency(unit.rent_amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Due Date</span>
                <span className="text-white">{unit.rent_due_day}st of each month</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Grace Period</span>
                <span className="text-white">{unit.grace_period_days} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Late Fee</span>
                <span className="text-white">{formatCurrency(unit.late_fee_amount)}</span>
              </div>
              <div className="border-t border-white/10 pt-3 mt-3">
                <div className="flex justify-between">
                  <span className="text-zinc-300 font-medium">Current Balance</span>
                  <span className={`font-mono font-bold text-lg ${rent_ledger.running_balance > 0 ? 'text-red-400' : rent_ledger.running_balance < 0 ? 'text-green-400' : 'text-white'}`}>
                    {rent_ledger.running_balance > 0 ? formatCurrency(rent_ledger.running_balance) : rent_ledger.running_balance < 0 ? `-${formatCurrency(Math.abs(rent_ledger.running_balance))}` : '$0.00'}
                  </span>
                </div>
                {rent_ledger.running_balance > 0 && (
                  <p className="text-red-400 text-xs mt-1">Amount owed - please pay promptly</p>
                )}
                {rent_ledger.running_balance < 0 && (
                  <p className="text-green-400 text-xs mt-1">Credit on account</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Lease Info Card */}
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-blue-500" /> Lease Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-zinc-500">Lease Start</span>
                <span className="text-white">{lease.start_date || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Lease End</span>
                <span className="text-white">{lease.end_date || 'Month-to-month'}</span>
              </div>
              {lease.end_date && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">Days Remaining</span>
                  <span className="text-white">
                    {Math.max(0, Math.ceil((new Date(lease.end_date) - new Date()) / (1000 * 60 * 60 * 24)))} days
                  </span>
                </div>
              )}
              {lease.documents && lease.documents.length > 0 && (
                <div className="border-t border-white/10 pt-3 mt-3">
                  <p className="text-zinc-400 text-sm mb-2">Documents</p>
                  {lease.documents.map((doc, i) => (
                    <a 
                      key={i}
                      href={doc.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-primary hover:underline text-sm"
                    >
                      <FileText className="w-3 h-3" /> {doc.document_name}
                    </a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Security Deposit Card */}
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Shield className="w-5 h-5 text-amber-500" /> Security Deposit
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-zinc-500">Original Deposit</span>
                <span className="text-white font-mono">{formatCurrency(lease.deposit_amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Status</span>
                <Badge className={`${lease.deposit_held ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                  {lease.deposit_held ? 'Held' : 'Refunded'}
                </Badge>
              </div>
              {lease.deposit_deductions && lease.deposit_deductions.length > 0 && (
                <>
                  <div className="border-t border-white/10 pt-3 mt-3">
                    <p className="text-zinc-400 text-sm mb-2">Deductions</p>
                    {lease.deposit_deductions.map((d, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-zinc-400">{d.reason}</span>
                        <span className="text-red-400">-{formatCurrency(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between border-t border-white/10 pt-2">
                    <span className="text-zinc-300 font-medium">Balance</span>
                    <span className="text-white font-mono font-bold">{formatCurrency(lease.deposit_balance)}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment History Table */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" /> {rent_ledger.year} Payment History
            </CardTitle>
            <CardDescription className="text-zinc-500">
              Your rent payment status for each month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-zinc-500">Month</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500">Rent</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500">Adjustments</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500">Total Due</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500">Paid</th>
                    <th className="text-center p-3 text-xs uppercase tracking-wider text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rent_ledger.entries.map((entry) => (
                    <tr key={entry.month} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-3 text-white font-medium">{entry.month_name}</td>
                      <td className="p-3 text-right font-mono text-zinc-300">{formatCurrency(entry.rent_due)}</td>
                      <td className={`p-3 text-right font-mono ${entry.adjustments > 0 ? 'text-red-400' : entry.adjustments < 0 ? 'text-green-400' : 'text-zinc-500'}`}>
                        {entry.adjustments !== 0 ? (entry.adjustments > 0 ? '+' : '') + formatCurrency(entry.adjustments) : '-'}
                      </td>
                      <td className="p-3 text-right font-mono text-white">{formatCurrency(entry.total_due)}</td>
                      <td className="p-3 text-right font-mono text-green-400">{formatCurrency(entry.amount_paid)}</td>
                      <td className="p-3 text-center">
                        <Badge className={`${getStatusBadge(entry.status)} inline-flex items-center gap-1`}>
                          {getStatusIcon(entry.status)}
                          {entry.status.toUpperCase()}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Need Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-zinc-400 mb-4">
              For questions about your lease, payments, or maintenance requests, please contact your property manager.
            </p>
            <div className="flex flex-wrap gap-4">
              {data.tenant_email && (
                <a href={`mailto:${data.tenant_email}`} className="flex items-center gap-2 text-primary hover:underline">
                  <Mail className="w-4 h-4" /> Contact via Email
                </a>
              )}
              {data.tenant_phone && (
                <a href={`tel:${data.tenant_phone}`} className="flex items-center gap-2 text-primary hover:underline">
                  <Phone className="w-4 h-4" /> {data.tenant_phone}
                </a>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-zinc-600 text-sm py-4">
          <p>This portal link expires on {new Date(data.portal_expires_at).toLocaleDateString()}</p>
          <p>Powered by WealthHub Property Management</p>
        </div>
      </div>
    </div>
  );
}
